import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, Check, Upload, Crop, Eye, X } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import { useTheme } from "../context/ThemeContext";
import {
  getMyDirectoryListing,
  updateMyDirectoryListing,
} from "../services/directoryService";
import { US_STATES, PA_OPTIONS } from "../constants/directoryOptions";
import { formatUsPhone, nextPhoneValue } from "../utils/phone";
import "./Directory.css";
import { DEFAULT_FOCUS, readFocus } from "../utils/photoFocus";
import PhotoCropper from "./PhotoCropper";
import ListingCard from "./ListingCard";
import "./MyDirectoryListing.css";

const PA_MAX = 3;
/** Matches the server cap in backend/routes/directory.js. */
const PA_OTHER_MAX = 35;
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Self-serve edit form for a live directory listing.
 *
 * Everything saved here goes live immediately — including practice areas.
 * The listing has already been through review, and re-queueing it for a
 * corrected phone number would take a live listing off the directory for no
 * reason.
 */
export default function EditMyListing() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [featured, setFeatured] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    firmName: "",
    city: "",
    state: "",
    yearsInPractice: "",
    role: "Attorney",
    emailPublic: "",
    publishEmail: false,
    phone: "",
    publishPhone: false,
    website: "",
    freeConsultation: false,
    practiceAreas: [],
    practiceAreaOther: "",
    photoUrl: "",
  });
  // A newly chosen photo, held as a data URL until save.
  const [photoData, setPhotoData] = useState("");
  // Where the subject sits in the photo, as percentages. Saved with the
  // listing so every container can crop around it instead of guessing.
  const [focus, setFocus] = useState(DEFAULT_FOCUS);
  // The picked file, held while the owner chooses their crop.
  const [cropSrc, setCropSrc] = useState("");
  // Shows the listing as a visitor will see it, without leaving the form.
  const [previewOpen, setPreviewOpen] = useState(false);
  const focusBoxRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const listing = await getMyDirectoryListing(authHeaders());
        if (!listing) {
          navigate("/directory?join=1", { replace: true });
          return;
        }
        setFeatured(!!listing.featured);
        setForm({
          firstName: listing.firstName || "",
          lastName: listing.lastName || "",
          firmName: listing.firmName || "",
          city: listing.city || "",
          state: listing.state || "",
          yearsInPractice: listing.yearsInPractice || "",
          role: listing.role || "Attorney",
          emailPublic: listing.emailPublic || "",
          publishEmail: !!listing.publishEmail,
          phone: formatUsPhone(listing.phone || listing.phonePublic || ""),
          publishPhone: !!listing.publishPhone,
          website: listing.website || "",
          freeConsultation: !!listing.freeConsultation,
          practiceAreas: listing.practiceAreas || [],
          practiceAreaOther: listing.practiceAreaOther || "",
          photoUrl: listing.photoUrl || "",
        });
        setFocus(readFocus(listing));
      } catch (err) {
        setError(err.message || "We could not load your listing.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const toggleArea = (area) => {
    setForm((prev) => {
      const has = prev.practiceAreas.includes(area);
      if (!has && prev.practiceAreas.length >= PA_MAX) return prev;
      return {
        ...prev,
        practiceAreas: has
          ? prev.practiceAreas.filter((a) => a !== area)
          : [...prev.practiceAreas, area],
      };
    });
    setSaved(false);
  };

  /**
   * Point in the preview the pointer is over, as percentages. The preview is
   * the same shape as the public card, so what is framed here is what a
   * visitor sees.
   */
  const focusFromEvent = (e) => {
    const box = focusBoxRef.current;
    if (!box) return null;
    const r = box.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const clamp = (n) => Math.min(100, Math.max(0, n));
    return {
      x: Math.round(clamp(((e.clientX - r.left) / r.width) * 100) * 10) / 10,
      y: Math.round(clamp(((e.clientY - r.top) / r.height) * 100) * 10) / 10,
    };
  };

  const moveFocus = (e) => {
    const next = focusFromEvent(e);
    if (!next) return;
    setFocus(next);
    setSaved(false);
  };

  const onFocusPointerDown = (e) => {
    // Click to place, drag to fine-tune; capture so the drag survives the
    // pointer leaving the box.
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    moveFocus(e);
  };

  const onFocusPointerMove = (e) => {
    if (draggingRef.current) moveFocus(e);
  };

  const onFocusPointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Keyboard equivalent, so the focal point is not mouse-only.
  const onFocusKeyDown = (e) => {
    const step = e.shiftKey ? 10 : 2;
    const moves = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    const clamp = (n) => Math.min(100, Math.max(0, n));
    setFocus((prev) => ({
      x: clamp(prev.x + move[0]),
      y: clamp(prev.y + move[1]),
    }));
    setSaved(false);
  };

  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setError("Please choose a photo under 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      // Straight into the cropper rather than accepting the file as-is: the
      // public well is landscape, so a portrait taken on a phone loses most of
      // its height and that is how a face gets cut off.
      setCropSrc(String(reader.result || ""));
      setError("");
    };
    reader.readAsDataURL(file);
    // Clear the input so picking the same file twice still fires a change.
    e.target.value = "";
  };

  /** The crop is already the well's shape, so it needs no further offsetting. */
  const onCropDone = (dataUrl) => {
    setPhotoData(dataUrl);
    setFocus({ x: 50, y: 50 });
    setCropSrc("");
    setSaved(false);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      setSaved(false);

      if (!form.firstName.trim() || !form.lastName.trim()) {
        setError("First and last name are required.");
        return;
      }
      if (!form.state.trim()) {
        setError("State is required.");
        return;
      }

      try {
        setSaving(true);
        const result = await updateMyDirectoryListing(
          {
            ...form,
            ...(photoData ? { photo: photoData } : {}),
            photoFocusX: focus.x,
            photoFocusY: focus.y,
          },
          authHeaders(),
        );

        // Take the saved photo URL from the response before dropping the local
        // data URL. Clearing photoData on its own left the preview falling back
        // to the photoUrl this form was loaded with — empty on a first upload —
        // so a save that had worked looked like it had not, until the page was
        // revisited and the listing reloaded.
        const savedUrl = result?.listing?.photoUrl;
        if (savedUrl) setForm((prev) => ({ ...prev, photoUrl: savedUrl }));
        if (savedUrl || !photoData) setPhotoData("");
        setSaved(true);
      } catch (err) {
        setError(err.message || "We could not save your changes.");
      } finally {
        setSaving(false);
      }
    },
    [form, photoData, focus],
  );

  const field = {
    width: "100%",
    padding: "11px 13px",
    borderRadius: "10px",
    border: `1px solid ${darkMode ? "rgba(255,255,255,0.14)" : "#cbd5e1"}`,
    background: darkMode ? "#0f172a" : "#fff",
    color: darkMode ? "#e2e8f0" : "#0f172a",
    fontSize: "15px",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  return (
    <div className={`directory-container ${darkMode ? "dark" : ""}`}>
      <SEO
        title="Edit My Directory Listing | LawFusion"
        path="/my-listing/edit"
        description="Update your LawFusion attorney directory listing."
        noIndex={true}
      />
      <Header />

      <main className="mdl-page">
        <div className="mdl-head">
          <h1>Edit Listing</h1>
        </div>
        <p className="mdl-sub">
          Your changes go live in the directory as soon as you save — no second
          review.
        </p>

        {loading ? (
          <div className="mdl-loading">
            <Loader size={22} className="fa-spin" />
            <p>Loading your listing…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="eml-form">
            <div className="eml-grid">
              <label>
                First name
                <input
                  type="text"
                  style={field}
                  value={form.firstName}
                  onChange={(e) => set("firstName", e.target.value)}
                />
              </label>
              <label>
                Last name
                <input
                  type="text"
                  style={field}
                  value={form.lastName}
                  onChange={(e) => set("lastName", e.target.value)}
                />
              </label>
              <label>
                Firm or organisation
                <input
                  type="text"
                  style={field}
                  value={form.firmName}
                  onChange={(e) => set("firmName", e.target.value)}
                />
              </label>
              <label>
                Role
                <select
                  style={field}
                  value={form.role}
                  onChange={(e) => set("role", e.target.value)}
                >
                  <option value="Attorney">Attorney</option>
                  <option value="Paralegal">Paralegal</option>
                </select>
              </label>
              <label>
                City
                <input
                  type="text"
                  style={field}
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                />
              </label>
              <label>
                State
                <select
                  style={field}
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                >
                  <option value="">Select a state</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Years in practice
                <input
                  type="text"
                  style={field}
                  value={form.yearsInPractice}
                  onChange={(e) => set("yearsInPractice", e.target.value)}
                  placeholder="e.g. 15+"
                />
              </label>
              <label>
                Website
                <input
                  type="text"
                  style={field}
                  value={form.website}
                  onChange={(e) => set("website", e.target.value)}
                  placeholder="yourfirm.com"
                />
              </label>
              <label>
                Public contact email
                <input
                  type="email"
                  style={field}
                  value={form.emailPublic}
                  onChange={(e) => set("emailPublic", e.target.value)}
                />
              </label>
              <label>
                Phone
                <input
                  type="tel"
                  style={field}
                  value={form.phone}
                  onChange={(e) =>
                    set("phone", nextPhoneValue(form.phone, e.target.value))
                  }
                />
              </label>
            </div>

            <div className="eml-checks">
              <label>
                <input
                  type="checkbox"
                  checked={form.publishEmail}
                  onChange={(e) => set("publishEmail", e.target.checked)}
                />
                Show my contact email on the listing
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.publishPhone}
                  onChange={(e) => set("publishPhone", e.target.checked)}
                />
                Show my phone number on the listing
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.freeConsultation}
                  onChange={(e) => set("freeConsultation", e.target.checked)}
                />
                I offer a free consultation
              </label>
            </div>

            <fieldset className="eml-areas">
              <legend>
                Practice areas{" "}
                <span>
                  ({form.practiceAreas.length} of {PA_MAX} selected)
                </span>
              </legend>
              <div className="eml-area-list">
                {[...PA_OPTIONS, "Other"].map((area) => {
                  const checked = form.practiceAreas.includes(area);
                  const disabled =
                    !checked && form.practiceAreas.length >= PA_MAX;
                  return (
                    <label
                      key={area}
                      className={`eml-area${disabled ? " disabled" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleArea(area)}
                      />
                      {area}
                    </label>
                  );
                })}
              </div>
              {form.practiceAreas.length >= PA_MAX && (
                // At the maximum every unchecked box greys out, which reads as
                // "this choice is locked in". It is not — unticking one frees a
                // slot — but nothing on screen said so.
                <p className="eml-area-hint">
                  That is the maximum of {PA_MAX}. Untick one to choose a
                  different practice area.
                </p>
              )}
              {form.practiceAreas.includes("Other") && (
                <input
                  type="text"
                  style={{ ...field, marginTop: "10px" }}
                  value={form.practiceAreaOther}
                  /* Same cap as signup and the server: a custom entry should
                     be about as long as the built-in labels. */
                  maxLength={PA_OTHER_MAX}
                  onChange={(e) =>
                    set("practiceAreaOther", e.target.value.slice(0, PA_OTHER_MAX))
                  }
                  placeholder="Describe your other practice area"
                />
              )}
            </fieldset>

            {featured && (
              <div className="eml-photo">
                {/* Shaped like the public card, so what is framed here is
                    what a visitor sees. Click or drag to say where the subject
                    is; every container then crops around that point instead of
                    a fixed guess that cut faces off. */}
                <div
                  className={`eml-photo-preview${
                    photoData || form.photoUrl ? " is-adjustable" : ""
                  }`}
                  ref={focusBoxRef}
                  {...(photoData || form.photoUrl
                    ? {
                        role: "slider",
                        tabIndex: 0,
                        "aria-label":
                          "Photo focal point. Click or drag to reposition, or use the arrow keys.",
                        "aria-valuetext": `Horizontal ${Math.round(
                          focus.x,
                        )}%, vertical ${Math.round(focus.y)}%`,
                        onPointerDown: onFocusPointerDown,
                        onPointerMove: onFocusPointerMove,
                        onPointerUp: onFocusPointerUp,
                        onPointerCancel: onFocusPointerUp,
                        onKeyDown: onFocusKeyDown,
                      }
                    : {})}
                >
                  {photoData || form.photoUrl ? (
                    <>
                      <img
                        src={photoData || form.photoUrl}
                        alt="Your listing"
                        style={{ objectPosition: `${focus.x}% ${focus.y}%` }}
                        draggable={false}
                      />
                      <span
                        className="eml-focus-dot"
                        style={{ left: `${focus.x}%`, top: `${focus.y}%` }}
                        aria-hidden="true"
                      />
                    </>
                  ) : (
                    <span>No photo yet</span>
                  )}
                </div>
                <div>
                  <p className="eml-photo-title">Listing photo</p>
                  <p className="eml-photo-copy">
                    Featured listings show your photo with a Featured badge.
                    JPG or PNG, under 5 MB.
                  </p>
                  {(photoData || form.photoUrl) && (
                    <p className="eml-photo-copy">
                      Drag on the photo to fine-tune what stays in frame, or
                      re-crop it. Preview shows the card as a visitor sees it.
                    </p>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onPhotoPick}
                    style={{ display: "none" }}
                  />
                  <div className="eml-photo-actions">
                    <button
                      type="button"
                      className="mdl-btn mdl-btn-secondary"
                      onClick={() => fileRef.current?.click()}
                    >
                      <Upload size={16} />{" "}
                      {photoData || form.photoUrl ? "Replace photo" : "Choose photo"}
                    </button>
                    {/* Re-crop without re-picking the file. Only the newly
                        cropped image can be re-cropped from memory; a stored
                        one is re-fetched by the img in the cropper. */}
                    {(photoData || form.photoUrl) && (
                      <button
                        type="button"
                        className="mdl-btn mdl-btn-secondary"
                        onClick={() => setCropSrc(photoData || form.photoUrl)}
                      >
                        <Crop size={16} /> Crop
                      </button>
                    )}
                    <button
                      type="button"
                      className="mdl-btn mdl-btn-secondary"
                      onClick={() => setPreviewOpen(true)}
                    >
                      <Eye size={16} /> Preview listing
                    </button>
                  </div>
                </div>
              </div>
            )}

            {error && <div className="eml-error">{error}</div>}
            {saved && (
              <div className="eml-saved">
                <Check size={16} /> Saved — your listing is live.
              </div>
            )}

            <div className="mdl-actions">
              <button
                type="submit"
                className="mdl-btn mdl-btn-primary"
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
              <button
                type="button"
                className="mdl-btn mdl-btn-secondary"
                onClick={() => navigate("/my-listing")}
              >
                Back to My Listing
              </button>
            </div>
          </form>
        )}
        {/* Crop before upload: the well is landscape, so a portrait dropped
            straight in loses most of its height. */}
        {cropSrc && (
          <PhotoCropper
            src={cropSrc}
            onCancel={() => setCropSrc("")}
            onCrop={onCropDone}
          />
        )}

        {/* The card exactly as a visitor gets it, built from what is on the
            form right now — including a crop that has not been saved yet. */}
        {previewOpen && (
          <div
            className="eml-preview-backdrop"
            onMouseDown={(e) => e.target === e.currentTarget && setPreviewOpen(false)}
          >
            <div className="eml-preview-modal" role="dialog" aria-modal="true" aria-label="Listing preview">
              <button
                type="button"
                className="eml-preview-close"
                onClick={() => setPreviewOpen(false)}
                aria-label="Close preview"
              >
                <X size={18} />
              </button>
              <h2 className="eml-preview-title">How your listing will look</h2>
              <p className="eml-preview-copy">
                Unsaved changes are included. Save the form to publish them.
              </p>
              <ListingCard
                listing={{
                  ...form,
                  featured,
                  photoUrl: photoData || form.photoUrl,
                  photoFocusX: focus.x,
                  photoFocusY: focus.y,
                }}
              />
            </div>
          </div>
        )}

      </main>

      <Footer />
    </div>
  );
}
